import React from 'react';

export interface AiTuneChangeRow {
  field?: string;
  label?: string;
  before?: string | null;
  after?: string | null;
}

export interface ParsedAiTuneAuditDetails {
  summary: string;
  changes: AiTuneChangeRow[];
  payload: Record<string, unknown> | null;
}

export function parseAiTuneAuditDetails(details?: string | null): ParsedAiTuneAuditDetails {
  if (!details || !details.trim()) {
    return { summary: '-', changes: [], payload: null };
  }

  const trimmed = details.trim();
  if (!trimmed.startsWith('{')) {
    return { summary: trimmed, changes: [], payload: null };
  }

  try {
    const payload = JSON.parse(trimmed) as Record<string, unknown>;
    const changes = Array.isArray(payload.changes)
      ? (payload.changes as AiTuneChangeRow[])
      : [];
    const directSummary = payload.summary ? String(payload.summary) : '';
    const regime = payload.marketRegime ? String(payload.marketRegime) : '';
    const outcome = payload.outcome ? String(payload.outcome) : '';
    const trigger = payload.trigger ? String(payload.trigger) : '';
    const signal = payload.signal ? String(payload.signal) : '';
    const fallbackParts = [signal, regime, outcome, trigger].filter(Boolean);
    const summary = directSummary || (fallbackParts.length > 0
      ? fallbackParts.join(' · ')
      : `${changes.length} field change(s)`);
    return { summary, changes, payload };
  } catch {
    return { summary: trimmed, changes: [], payload: null };
  }
}

export function isAiTuneAuditAction(action?: string | null): boolean {
  if (!action) return false;
  return action.startsWith('AI_AUTO_TUNE') || action === 'SCHEDULED_AI_AUTO_TUNE';
}

export function isMarketSignalAuditAction(action?: string | null): boolean {
  if (!action) return false;
  return action.startsWith('MARKET_SIGNAL_');
}

interface AiTuneChangeTableProps {
  changes: AiTuneChangeRow[];
  compact?: boolean;
}

export const AiTuneChangeTable: React.FC<AiTuneChangeTableProps> = ({ changes, compact = false }) => {
  if (!changes.length) {
    return <span style={{ color: '#6b7280', fontSize: 12 }}>No field-level diff recorded.</span>;
  }

  const cellPad = compact ? 6 : 8;

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? 12 : 13, marginTop: compact ? 4 : 8 }}>
      <thead>
        <tr style={{ background: '#f8fafc' }}>
          <th style={{ textAlign: 'left', padding: cellPad, borderBottom: '1px solid #e2e8f0' }}>Field</th>
          <th style={{ textAlign: 'left', padding: cellPad, borderBottom: '1px solid #e2e8f0' }}>Before</th>
          <th style={{ textAlign: 'left', padding: cellPad, borderBottom: '1px solid #e2e8f0' }}>After</th>
        </tr>
      </thead>
      <tbody>
        {changes.map((row, idx) => (
          <tr key={`${row.field || row.label || idx}-${idx}`}>
            <td style={{ padding: cellPad, borderBottom: '1px solid #f1f5f9', fontWeight: 600, color: '#334155' }}>
              {row.label || row.field || '—'}
            </td>
            <td style={{ padding: cellPad, borderBottom: '1px solid #f1f5f9', color: '#b91c1c' }}>
              {row.before ?? '—'}
            </td>
            <td style={{ padding: cellPad, borderBottom: '1px solid #f1f5f9', color: '#15803d' }}>
              {row.after ?? '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default AiTuneChangeTable;
